import { X, RefreshCw, Plug } from 'lucide-react';
import { MiroIcon } from './IntegrationIcons';
import type { MiroConnection } from './useMiroConnection';

export function MiroConfigModal({ miro }: { miro: MiroConnection }) {
  const {
    showMiroConfig,
    setShowMiroConfig,
    miroAccessToken,
    setMiroAccessToken,
    miroTeamId,
    setMiroTeamId,
    miroConnected,
    handleMiroDisconnect,
    handleMiroConnect,
    isConnecting,
  } = miro;

  return (
    <>
      {/* Miro Configuration Modal */}
      {showMiroConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-md w-full mx-4">
            <div className="p-4 border-b border-[var(--rule)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--mustard-dim)]">
                  <MiroIcon />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">Connect to Miro</h2>
                  <p className="text-sm text-[var(--text-ter)]">Enter your Miro access token</p>
                </div>
              </div>
              <button onClick={() => setShowMiroConfig(false)} className="p-2 hover:bg-[var(--hover-tint)]">
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Access Token <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="password"
                  value={miroAccessToken}
                  onChange={(e) => setMiroAccessToken(e.target.value)}
                  placeholder="Enter your Miro access token"
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500 focus:border-[var(--mustard)]"
                />
                <p className="text-xs text-[var(--text-ter)] mt-1">
                  Get your token from{' '}
                  <a
                    href="https://miro.com/app/settings/user-profile/apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--mustard)] hover:underline"
                  >
                    Miro Developer Settings
                  </a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Team ID <span className="text-[var(--text-ter)]">(optional)</span>
                </label>
                <input
                  type="text"
                  value={miroTeamId}
                  onChange={(e) => setMiroTeamId(e.target.value)}
                  placeholder="Your Miro team ID"
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500 focus:border-[var(--mustard)]"
                />
              </div>

              {miroConnected && (
                <button
                  onClick={handleMiroDisconnect}
                  className="w-full px-4 py-2 text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors text-sm"
                >
                  Disconnect from Miro
                </button>
              )}
            </div>

            <div className="p-4 border-t border-[var(--rule)] flex justify-end gap-3">
              <button
                onClick={() => setShowMiroConfig(false)}
                className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMiroConnect}
                disabled={!miroAccessToken || isConnecting}
                className="px-4 py-2 bg-[var(--mustard)] hover:bg-[var(--mustard)] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isConnecting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Plug className="h-4 w-4" />
                )}
                Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
